import React, { useEffect, useState } from "react";

import { X } from "lucide-react";

import GuidanceLetsGoButton from "./GuidanceLetsGoButton";
import { useTodaysGuidance } from "./useTodaysGuidance";
import { useAppSelector } from "../../Store/Hooks";
import { CADENCE_BADGE_STYLES } from "../../Utils/Guidance/guidanceCadence";
import { openGuidanceDrawer } from "../../Utils/Guidance/guidanceDrawerBus";
import { isGuidanceHubActiveForUser } from "../../Utils/Guidance/guidanceHubSettings";
import CommonModal from "../Modal";

const DISMISSED_DATE_KEY = "eb_guidance_modal_dismissed_date";
const BULLET_PREVIEW_COUNT = 3;

const todayIso = () => new Date().toISOString().slice(0, 10);

interface DailyPlaybookModalProps {
  userRole: string | { name?: string } | null | undefined;
}

/**
 * Focused, once-per-day interrupt on home page load (design-note.md §4.4) —
 * distinct from the always-visible TodaysPlaybookWidget: this is a
 * "pause and commit" moment, not a persistent reference.
 */
const DailyPlaybookModal: React.FC<DailyPlaybookModalProps> = ({
  userRole,
}) => {
  const { hubEnabled, hubEnabledRoles, hubEnabledLoaded } = useAppSelector(
    state => state.guidance
  );
  const hubActive = isGuidanceHubActiveForUser(
    hubEnabled,
    hubEnabledRoles,
    userRole
  );
  const { role, cadenceKey, cadenceLabel, playbook, personaContent, isReady } =
    useTodaysGuidance(userRole);
  const [isOpen, setIsOpen] = useState(false);
  const [showAllBullets, setShowAllBullets] = useState(false);

  useEffect(() => {
    // Wait for the org kill-switch to resolve. Default hubEnabled is true
    // until fetch completes — opening early would leave the modal stuck open
    // after the setting comes back disabled.
    if (!hubEnabledLoaded) return;
    if (!hubActive) {
      setIsOpen(false);
      return;
    }
    if (!isReady) return;
    try {
      const dismissedDate = localStorage.getItem(DISMISSED_DATE_KEY);
      if (dismissedDate !== todayIso()) {
        setIsOpen(true);
      }
    } catch {
      // localStorage unavailable (private browsing, blocked site data) —
      // fail open to "not shown" rather than throw.
    }
  }, [hubActive, hubEnabledLoaded, isReady]);

  const close = () => setIsOpen(false);

  const dismissForToday = () => {
    try {
      localStorage.setItem(DISMISSED_DATE_KEY, todayIso());
    } catch {
      // ignore — worst case the modal reappears this session
    }
    setIsOpen(false);
  };

  const letsGo = () => {
    dismissForToday();
    openGuidanceDrawer({ persona: role, cadenceKey });
  };

  const style = CADENCE_BADGE_STYLES[cadenceKey];
  const bullets = showAllBullets
    ? playbook.what
    : playbook.what.slice(0, BULLET_PREVIEW_COUNT);
  const hasMore = playbook.what.length > BULLET_PREVIEW_COUNT;

  return (
    <CommonModal
      isOpen={isOpen && isReady && hubActive && hubEnabledLoaded}
      toggle={close}
      showHeader={false}
      size="3xl"
      className="guidance-daily-modal"
      usePortal
    >
      <div className="relative">
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute -top-1 -right-1 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <p
              className="text-xs font-semibold uppercase tracking-wide mb-2"
              style={{ color: style.text }}
            >
              {cadenceLabel} Playbook · {playbook.time}
            </p>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">
              {playbook.heading}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {personaContent.personaLabel} · {personaContent.personaTagline}
            </p>

            <ul className="space-y-1.5 mb-2">
              {bullets.map((line, idx) => (
                <li key={idx} className="text-sm text-gray-700 flex gap-1.5">
                  <span className="text-gray-300 shrink-0">›</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            {hasMore && (
              <button
                type="button"
                onClick={() => setShowAllBullets(v => !v)}
                className="text-xs font-medium text-purple-600 hover:text-purple-700"
              >
                {showAllBullets
                  ? "Show less"
                  : `+${playbook.what.length - BULLET_PREVIEW_COUNT} more`}
              </button>
            )}
          </div>

          <GuidanceLetsGoButton cadenceKey={cadenceKey} onClick={letsGo} />
        </div>

        <button
          type="button"
          onClick={dismissForToday}
          className="mt-4 text-xs text-gray-400 hover:text-gray-600"
        >
          Don&apos;t show again today
        </button>
      </div>
    </CommonModal>
  );
};

export default DailyPlaybookModal;
